import { compareTracksForTimeline, getWorkoutName } from '@strength/domain';
import type { RouterOutput } from '../../../../trpc';

type CrossTeamResult = RouterOutput['plan']['crossTeamTracksForAthlete'][0];
type CrossTeamTrack = CrossTeamResult['tracks'][number];
type CrossTeamBlock = CrossTeamTrack['blocks'][number];
type CrossTeamWorkoutEvent = CrossTeamTrack['events'][number];
type CrossTeamPhase = CrossTeamResult['phases'][number];

/**
 * The Zero-path input rows, as `useZeroQuery` hands them back after SC-5647
 * normalization: every `date` column (Block.startDate/endDate, layer
 * assignedDate, Phase.startDate/endDate) is already a `yyyy-MM-dd` string in
 * UTC and every timestamp column an ISO string — including nested related
 * rows. Structurally declared here because NormalizedRow's recursive
 * HumanReadable types cannot be named directly.
 */
export type ZeroCrossTeamTrackRow = {
  id: string;
  name: string;
  teamId: string;
  parentTrackId: string | null;
  sortOrder: number | null;
  createdAt: string | null;
  blocks: ZeroCrossTeamBlockRow[];
};

export type ZeroCrossTeamBlockRow = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'planned' | 'canceled' | null;
  blockType: 'strength_training' | 'bowling' | null;
  cycleLabel: string;
  sessionLabel: string;
  athletePublishedWorkoutLayers: ZeroCrossTeamLayerRow[];
};

export type ZeroCrossTeamLayerRow = {
  id: string;
  workoutId: string;
  blockId: string;
  assignedDate: string | null;
  status: 'completed' | 'canceled' | 'planned' | 'skipped' | null;
  name: string | null;
  cycleSortOrder: number;
  sessionSortOrder: number;
};

export type ZeroCrossTeamPhaseRow = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  color: string;
  type: string | null;
  teamId: string;
};

export type TransformCrossTeamZeroRowsInput = {
  /** Track rows from `queries.track.crossTeamTracksForAthlete` (normalized). */
  tracks: readonly ZeroCrossTeamTrackRow[];
  /** Phase rows from `queries.track.crossTeamPhases` (normalized). */
  phases: readonly ZeroCrossTeamPhaseRow[];
  /**
   * The viewer's teams list (roster.teamsPreview). Names come from here, never
   * from Zero. The list filters `is_active = true` while the tRPC route does
   * not, so a track whose team is missing from it keeps its group and renders
   * under a fallback label (its team id) — the team keeps its place in the
   * sort so index-assigned colors stay stable.
   */
  teams: ReadonlyArray<{ id: string; name: string }>;
};

const compareByNameThenId = (
  a: { name: string; id: string },
  b: { name: string; id: string },
): number => a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

const compareByStartDate = (
  a: { startDate: string },
  b: { startDate: string },
) => a.startDate.localeCompare(b.startDate);

/**
 * Stable render order for cross-team phase bands, shared by BOTH data paths:
 * neither the tRPC phase query nor the Zero named query guarantees row order,
 * and BlockPanel stacks overlapping phases at the same position and z-index —
 * so the path that happens to deliver a different order would put a different
 * phase name/color on top. Sort by start date, tiebreak by id.
 */
export const comparePhasesForDisplay = (
  a: { startDate: string; id: string },
  b: { startDate: string; id: string },
): number => a.startDate.localeCompare(b.startDate) || a.id.localeCompare(b.id);

const compareByAssignedDate = (
  a: { assignedDate: string },
  b: { assignedDate: string },
) => a.assignedDate.localeCompare(b.assignedDate);

/**
 * Groups the cross-team Zero rows into the tRPC endpoint's result shape
 * (`CrossTeamResult[]`), with deterministic ordering the tRPC path does not
 * itself guarantee: teams by (name, id), tracks by
 * `compareTracksForTimeline`, blocks by start date, events by assigned date.
 * The parity test sorts the tRPC output the same way before comparing.
 */
export function transformCrossTeamZeroRows({
  tracks,
  phases,
  teams,
}: TransformCrossTeamZeroRowsInput): CrossTeamResult[] {
  const phasesByTeamId = new Map<string, CrossTeamPhase[]>();
  for (const phase of phases) {
    const entry = phasesByTeamId.get(phase.teamId) ?? [];
    entry.push({
      id: phase.id,
      name: phase.name,
      startDate: phase.startDate,
      endDate: phase.endDate,
      color: phase.color,
      type: phase.type ?? null,
    });
    phasesByTeamId.set(phase.teamId, entry);
  }

  // One entry per team that actually has a track for the athlete — teams
  // without tracks are dropped, matching the tRPC endpoint's final filter.
  const trackTeams = new Map<string, { id: string; name: string }>();
  for (const track of tracks) {
    if (!trackTeams.has(track.teamId)) {
      trackTeams.set(track.teamId, {
        id: track.teamId,
        name: teams.find((t) => t.id === track.teamId)?.name ?? track.teamId,
      });
    }
  }
  const orderedTeams = [...trackTeams.values()].sort(compareByNameThenId);

  return orderedTeams.map((team) => {
    const teamTracks = tracks
      .filter((t) => t.teamId === team.id)
      .sort((a, b) =>
        compareTracksForTimeline(
          { sortOrder: a.sortOrder ?? 0, createdAt: a.createdAt },
          { sortOrder: b.sortOrder ?? 0, createdAt: b.createdAt },
        ),
      )
      .map((track): CrossTeamTrack => {
        const blocks = track.blocks
          .map(
            (block): CrossTeamBlock => ({
              id: block.id,
              name: block.name,
              startDate: block.startDate,
              endDate: block.endDate,
              // DB columns are notNull with defaults ('planned' /
              // 'strength_training'); the Zero schema types them optional.
              status: block.status ?? 'planned',
              blockType: block.blockType ?? 'strength_training',
            }),
          )
          .sort(compareByStartDate);

        // A block's events live on its nested layers. Flex workouts (null
        // assignedDate) are excluded — they aren't placed on the calendar
        // grid, matching the tRPC endpoint.
        const events = track.blocks
          .flatMap((block) =>
            block.athletePublishedWorkoutLayers.map(
              (layer): CrossTeamWorkoutEvent => ({
                id: layer.id,
                workoutId: layer.workoutId,
                blockId: layer.blockId,
                trackId: track.id,
                // Computed client-side, the same way the tRPC endpoint
                // computes display names.
                workoutName: getWorkoutName(layer, block, false),
                assignedDate: layer.assignedDate ?? '',
                status: layer.status ?? 'planned',
              }),
            ),
          )
          .filter((event) => event.assignedDate !== '')
          .sort(compareByAssignedDate);

        return {
          id: track.id,
          name: track.name,
          parentTrackId: track.parentTrackId ?? null,
          blocks,
          events,
        };
      });

    return {
      teamId: team.id,
      teamName: team.name,
      tracks: teamTracks,
      phases: (phasesByTeamId.get(team.id) ?? []).sort(comparePhasesForDisplay),
    };
  });
}
